import type {
  Member,
  MemberRole,
  PrivacyCeilingName,
  PutMemberOptions,
  PutScopesOptions,
  ScopeGrants,
  ScopeLevel,
} from '@cef-ai/vault-sdk';

/**
 * The ORGANIZATIONS member-write client — invite/add a member and replace a
 * member's scope grants, on the SELECTED org vault. Sibling of `orgs.ts` for
 * the write side: every method here runs on the wallet-signing SDK
 * (`session.sdk`) and is called only from inside a click, AFTER
 * `session.signingHandle()` has resolved — both writes carry the owner's
 * `X-Grant-Signature` over a grant document the server rebuilds from exactly
 * the arguments this layer passes, so the shapes below are load-bearing, not
 * presentation.
 */

/**
 * The SDK surface these writes need, narrowed so unit tests can pass a plain
 * object and the wallet-signing `VaultSDK` satisfies it structurally.
 * `memberships` lives on the SDK root (NOT on the `Vault` handle that
 * `signingHandle()` resolves), which is why these calls take `session.sdk`
 * while the handle only pays the step-up ceremony inside the click.
 */
export interface OrgMembersWriteSdk {
  memberships: {
    put: (
      vaultId: string,
      memberPubkey: string,
      role: MemberRole,
      expiresAt?: string,
      opts?: PutMemberOptions,
    ) => Promise<Member>;
    putScopes: (
      vaultId: string,
      memberPubkey: string,
      scopes: ScopeGrants,
      opts?: PutScopesOptions,
    ) => Promise<void>;
  };
}

/** One editable grant level in the UI: a real `ScopeLevel`, or "no grant". */
export type LevelChoice = ScopeLevel | 'none';

/** The invite/add request: everything `memberships.put` needs from a form. */
export interface AddMemberRequest {
  memberPubkey: string;
  role: MemberRole;
  /** RFC 3339, already formatted by {@link toExpiryRfc3339} — exactly once. */
  expiresAt?: string;
}

/**
 * The member's wallet public key in the server's canonical spelling
 * (`0x` + 64 lowercase hex), or null when the input cannot be one. The server
 * canonicalises the URL parameter BEFORE it derives the grant-document bytes,
 * so a pasted bare or upper-case key must be canonicalised here or the write
 * addresses a member that does not exist.
 */
export function canonicalMemberPubkey(input: string): string | null {
  const trimmed = input.trim();
  const bare = trimmed.startsWith('0x') || trimmed.startsWith('0X') ? trimmed.slice(2) : trimmed;
  return /^[0-9a-fA-F]{64}$/.test(bare) ? `0x${bare.toLowerCase()}` : null;
}

/**
 * A date-picker day (`YYYY-MM-DD`) as the RFC 3339 instant access runs
 * THROUGH — the end of that day, UTC — or null when malformed. vault-api
 * parses `expiresAt` for a timestamptz column and 400s anything else, and the
 * string is INSIDE the signed document, so it is formatted exactly once, here.
 */
export function toExpiryRfc3339(date: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  return `${date}T23:59:59Z`;
}

/**
 * The standing half of `putScopes`' grant document, off the roster row:
 * role ALWAYS, `expiresAt` only when the row carries one — BOTH or NEITHER,
 * never role-alone for a membership that expires. The row's spelling travels
 * unchanged even though the server compares INSTANTS, not strings (any
 * spelling of the right instant is accepted): we already hold the right
 * instant as a string, and re-deriving it — parsing, then re-rendering —
 * could only move the moment (a rounded fraction, a shifted zone) or drop it
 * entirely, never improve it.
 */
export function memberStanding(member: Pick<Member, 'role' | 'expiresAt'>): {
  role: MemberRole;
  expiresAt?: string;
} {
  return member.expiresAt === undefined
    ? { role: member.role }
    : { role: member.role, expiresAt: member.expiresAt };
}

/**
 * The editor's per-scope choices as the wire record: every scope with a real
 * level keeps it, every "none" is DROPPED — `putScopes` replaces the full set
 * (delete-then-reinsert), so omission IS how a grant is revoked, and an empty
 * record is the real write "this member holds nothing".
 */
export function grantsFromLevels(levels: Record<string, LevelChoice>): ScopeGrants {
  const grants: ScopeGrants = {};
  for (const [name, choice] of Object.entries(levels)) {
    if (choice !== 'none') grants[name] = choice;
  }
  return grants;
}

/**
 * Invite (or, when the pubkey is already on the roster, re-role) a member of
 * the selected org vault. `PUT /vaults/:vaultId/members/:memberPubkey` via the
 * SDK — this is how a vault becomes an organization at all.
 */
export async function addOrgMember(
  sdk: OrgMembersWriteSdk,
  vaultId: string,
  req: AddMemberRequest,
): Promise<Member> {
  return sdk.memberships.put(vaultId, req.memberPubkey, req.role, req.expiresAt);
}

/**
 * Replace one member's FULL scope-grant set on the selected org vault, signing
 * from the request's scopes and the member's standing as read off the roster
 * row. The row's spelling — not a re-derivation — is what makes the signed
 * document match what the server holds.
 */
export async function saveOrgMemberGrants(
  sdk: OrgMembersWriteSdk,
  vaultId: string,
  member: Pick<Member, 'memberPubkey' | 'role' | 'expiresAt'>,
  scopes: ScopeGrants,
): Promise<void> {
  return sdk.memberships.putScopes(vaultId, member.memberPubkey, scopes, {
    standing: memberStanding(member),
  });
}

/**
 * Set (never clear) one member's privacy ceiling on the selected org vault.
 * A ceiling write RE-SIGNS the member's whole grant: `memberships.put`
 * rebuilds the document from the scopes the member already holds, the
 * standing sent here, and the ceiling option — which flips the document to
 * its v2 schema — so the standing must travel exactly as the grants editor
 * sends it (role always, `expiresAt` only when the row carries one, both or
 * neither) or the server refuses a document it did not derive. The held
 * scopes are left to the SDK's own read-back: a row-derived set could be
 * stale, and a stale set is a 400, not a fresher document.
 *
 * Clearing is deliberately not offered: with the option unset `put` signs a
 * v1 document UNCONDITIONALLY (4.2.0 has no existing-ceiling check), and
 * what the server then does for a member who already holds one — treat the
 * v1 document as "no ceiling" or refuse the omit-as-clear — is an undecided
 * semantic. A downgrade path is that server-semantics follow-up, not a UI
 * choice.
 */
export async function setOrgMemberCeiling(
  sdk: OrgMembersWriteSdk,
  vaultId: string,
  member: Pick<Member, 'memberPubkey' | 'role' | 'expiresAt'>,
  ceiling: PrivacyCeilingName,
): Promise<Member> {
  return sdk.memberships.put(vaultId, member.memberPubkey, member.role, member.expiresAt, {
    privacyCeiling: ceiling,
  });
}
